import { MouseEventHandler, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useDataMutation, useInaccessibleFields } from '@work4all/data';

import { COLUMNS_BY_GENERAL_SETTINGS_TYPE } from '@work4all/models/lib/constants';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { signedIntToHex } from '@work4all/utils/lib/colorConversion';

import { IConvertPopoverProps } from '../../convert-popover/ConvertPopover';
import { InfoCard } from '../components/info-card/InfoCard';
import { InfoCards } from '../components/info-cards/InfoCards';
import { PreviewInputsProvider } from '../components/preview-input';
import { PreviewTitle } from '../components/preview-title/PreviewTitle';
import { PreviewWrapper } from '../components/preview-wrapper/PreviewWrapper';
import { useEntityPreview } from '../hooks/use-entity-preview';

type Entity = { id?: number | string | null; name?: string };
export type Props<T extends Entity> = {
  entries: T[];
  entityType: Entities;
  onCloseClick?: MouseEventHandler<HTMLButtonElement>;
  onEditClicked?: MouseEventHandler<HTMLButtonElement>;
  onShareClicked?: () => void;
  convertProps?: Pick<IConvertPopoverProps, 'exclude' | 'onClick'>;
  onVisibilityToggle?: (visible: boolean) => void;
};

// Instead of copying around we can use single component for all General settings
export function GeneralSettingsPreview<T extends Entity>(props: Props<T>) {
  const {
    entries,
    entityType,
    onCloseClick,
    onEditClicked,
    onShareClicked,
    convertProps,
    onVisibilityToggle,
  } = props;

  const { t } = useTranslation();
  const { isInaccessible } = useInaccessibleFields();

  const nameIsAccessible = !isInaccessible(entityType, 'name');

  const [mutate] = useDataMutation<T, EMode.upsert>({
    entity: entityType,
    mutationType: EMode.upsert,
    responseData: {
      id: null,
    } as T,
  });

  const entityPreview = useEntityPreview({
    subEntityType: entityType,
    entries,
    mutate,
  });

  const titleRef = useRef<PreviewTitle>(null);

  const entity = entries[0];
  const canEdit = true;

  return (
    <PreviewInputsProvider
      {...entityPreview}
      entity={entityType}
      entries={entries}
    >
      <PreviewWrapper>
        <PreviewTitle
          disabled={true}
          canEdit={canEdit}
          ref={titleRef}
          label={entity?.name}
          onCloseClick={onCloseClick}
          onEditClicked={onEditClicked}
          onShareClicked={onShareClicked}
          convertProps={convertProps}
          onVisibilityToggle={onVisibilityToggle}
        >
          {entity?.name}
        </PreviewTitle>

        <InfoCards columns={2}>
          {nameIsAccessible && (
            <InfoCard staticField label={t('COMMON.NAME')}>
              {entity?.name}
            </InfoCard>
          )}
          {/* Render additional fields based on COLUMNS_BY_GENERAL_SETTINGS_TYPE */}
          {COLUMNS_BY_GENERAL_SETTINGS_TYPE[entityType]?.map((fieldName) => {
            const fieldValue = (entity as Record<string, unknown>)?.[fieldName];
            const isFieldAccessible = !isInaccessible(entityType, fieldName);

            if (!isFieldAccessible) {
              return null;
            }

            return (
              <InfoCard
                key={fieldName}
                staticField
                label={t(`COMMON.${fieldName.toUpperCase()}`)}
              >
                {fieldName === 'color' ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: signedIntToHex(
                          fieldValue as string | number
                        ),
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                    />
                    <span>{signedIntToHex(fieldValue as string | number)}</span>
                  </div>
                ) : (
                  String(fieldValue)
                )}
              </InfoCard>
            );
          })}
        </InfoCards>
      </PreviewWrapper>
    </PreviewInputsProvider>
  );
}
